import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, Phone } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useSettings } from "@/hooks/useSettings";
import Breadcrumbs from "@/components/Breadcrumbs";
import JsonLd from "@/components/JsonLd";
import { API, fileSrc, telHref } from "@/lib/api";
import { fmtPostDate } from "@/components/BlogCard";

function renderContent(content) {
  return content.split(/\n{2,}/).map((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("## ")) {
      return <h2 key={i} className="text-2xl sm:text-3xl font-semibold text-white mt-10 mb-4">{trimmed.slice(3)}</h2>;
    }
    if (trimmed.split("\n").every((l) => l.trim().startsWith("- "))) {
      return (
        <ul key={i} className="space-y-2.5 my-5">
          {trimmed.split("\n").map((l, j) => (
            <li key={j} className="flex items-start gap-3 text-gray-300 leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2.5 shrink-0" />
              {l.trim().slice(2)}
            </li>
          ))}
        </ul>
      );
    }
    return <p key={i} className="text-gray-300 leading-relaxed my-5">{trimmed}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const settings = useSettings();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useSEO({
    title: post ? `${post.title} | taxipark.ge ბლოგი` : "ბლოგი | taxipark.ge",
    description: post?.excerpt || undefined,
    path: `/blog/${slug}`,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <main className="pt-40 pb-24 text-center" data-testid="blog-post-not-found">
        <p className="text-gray-400 text-lg">სტატია ვერ მოიძებნა.</p>
        <Link to="/blog" className="btn-gold mt-8">ბლოგში დაბრუნება</Link>
      </main>
    );
  }
  if (!post) {
    return <main className="pt-40 pb-24 text-center text-gray-500">იტვირთება...</main>;
  }

  const phone = settings.phone || "591 063 123";
  const coverAbs = post.cover_url ? `https://taxipark.ge${post.cover_url}` : undefined;

  return (
    <main className="min-h-screen bg-[#0A0B0E] pt-28 pb-20" data-testid="blog-post-page">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.excerpt,
        image: coverAbs ? [coverAbs] : undefined,
        datePublished: post.created_at,
        dateModified: post.updated_at,
        author: { "@type": "Organization", name: "taxipark.ge", url: "https://taxipark.ge/" },
        publisher: { "@type": "Organization", name: "taxipark.ge", url: "https://taxipark.ge/" },
        mainEntityOfPage: `https://taxipark.ge/blog/${post.slug}`,
      }} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <Breadcrumbs items={[{ name: "მთავარი", path: "/" }, { name: "ბლოგი", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }]} />

        <motion.article initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-sm text-gray-500 flex items-center gap-1.5 mb-4">
            <CalendarDays className="w-4 h-4 text-[#D4AF37]" /> {fmtPostDate(post.created_at)}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">{post.title}</h1>
          {post.excerpt && <p className="text-lg text-gray-400 mt-5 leading-relaxed">{post.excerpt}</p>}

          {post.cover_url && (
            <div className="rounded-2xl overflow-hidden border border-white/10 mt-8">
              <img src={fileSrc(post.cover_url)} alt={`${post.title} — taxipark.ge`} className="w-full max-h-[420px] object-cover" data-testid="blog-post-cover" />
            </div>
          )}

          <div className="mt-8" data-testid="blog-post-content">{renderContent(post.content || "")}</div>
        </motion.article>

        <section className="mt-14 card-lux p-8 text-center" data-testid="blog-post-cta">
          <h2 className="text-xl sm:text-2xl font-semibold text-white">გსურს ტაქსში მუშაობის დაწყება?</h2>
          <p className="text-gray-400 mt-3">აირჩიე ავტომობილი ჩვენი ავტოპარკიდან და დაიწყე დღესვე.</p>
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <Link to="/catalog" className="btn-gold text-base">ავტომობილების ნახვა <ArrowRight className="w-5 h-5" /></Link>
            <a href={telHref(phone)} className="btn-ghost text-base"><Phone className="w-5 h-5" /> დაგვირეკე</a>
          </div>
        </section>
      </div>
    </main>
  );
}
